import { generateClient } from 'aws-amplify/api';
import { onInputStatusNotification } from '../graphql/subscriptions';

const client = generateClient();

const parseDataJson = (dataJson) => {
  if (!dataJson) return null;
  try {
    return JSON.parse(dataJson);
  } catch (e) {
    console.warn('Failed to parse notification dataJson:', e);
    return null;
  }
};

const state = {
  notifications: [],
  // Map of inputId -> subscription object
  subscriptions: {},
  // Map of inputId -> input name
  inputNames: {},
};

const getters = {
  unreadCount: (state) => state.notifications.filter((n) => !n.read).length,
  allNotifications: (state) => state.notifications.map(n => ({
    ...n,
    inputName: state.inputNames[n.inputId] || n.inputId
  })),
};

const mutations = {
  setInputNames(state, mapping) {
    state.inputNames = { ...state.inputNames, ...mapping };
  },
  addNotification(state, notification) {
    state.notifications.unshift({
      ...notification,
      data: parseDataJson(notification.dataJson),
      read: false,
      receivedAt: new Date().toISOString(),
    });
  },
  markAsRead(state, index) {
    if (state.notifications[index]) {
      state.notifications[index].read = true;
    }
  },
  markAllAsRead(state) {
    state.notifications.forEach((n) => { n.read = true; });
  },
  clearAll(state) {
    state.notifications = [];
  },
  setInputSubscription(state, { inputId, sub }) {
    state.subscriptions = { ...state.subscriptions, [inputId]: sub };
  },
  removeInputSubscription(state, inputId) {
    const { [inputId]: _, ...rest } = state.subscriptions;
    state.subscriptions = rest;
  },
  clearSubscriptions(state) {
    state.subscriptions = {};
  },
};

const actions = {
  subscribeToInput({ commit, dispatch, state }, inputId) {
    // Unsubscribe if already subscribed to this inputId
    if (state.subscriptions[inputId]) {
      try { state.subscriptions[inputId].unsubscribe(); } catch (_) { /* ignore */ }
      commit('removeInputSubscription', inputId);
    }

    try {
      const sub = client.graphql({
        query: onInputStatusNotification,
        variables: { inputId },
        authMode: 'userPool',
      }).subscribe({
        next: ({ data }) => {
          const notification = data?.onInputStatusNotification;
          if (notification) {
            commit('addNotification', notification);
          }
        },
        error: (err) => {
          console.error(`[Notifications] Subscription error for ${inputId}:`, err);
          commit('removeInputSubscription', inputId);
          setTimeout(() => dispatch('subscribeToInput', inputId), 5000);
        },
      });

      commit('setInputSubscription', { inputId, sub });
    } catch (err) {
      console.error(`[Notifications] Failed to subscribe to ${inputId}:`, err);
    }
  },

  subscribeToAll({ dispatch }, inputIds) {
    if (!Array.isArray(inputIds) || inputIds.length === 0) return;
    console.log(`[Notifications] Subscribing to ${inputIds.length} inputs`);
    inputIds.forEach((inputId) => dispatch('subscribeToInput', inputId));
  },

  unsubscribeAll({ state, commit }) {
    const count = Object.keys(state.subscriptions).length;
    Object.values(state.subscriptions).forEach((sub) => {
      try { sub.unsubscribe(); } catch (_) { /* ignore */ }
    });
    commit('clearSubscriptions');
    console.log(`[Notifications] Unsubscribed from ${count} input(s)`);
  },

  async subscribe({ commit, dispatch, state }) {
    try {
      // Avoid duplicate global subscriptions
      if (Object.keys(state.subscriptions).length > 0) return;

      const inputs = await dispatch('sourceMonitoring/fetchInputs', null, { root: true });
      let list = [];
      if (Array.isArray(inputs)) {
        list = inputs;
      } else if (inputs?.items && Array.isArray(inputs.items)) {
        list = inputs.items;
      } else if (inputs?.data?.listInputs?.items && Array.isArray(inputs.data.listInputs.items)) {
        list = inputs.data.listInputs.items;
      }

      console.log(`[Notifications] Found ${list.length} total inputs, filtering for MEDIA_CONNECT...`);

      const mediaConnectInputs = list.filter((input) => input.type === 'MEDIA_CONNECT');
      const inputNames = {};
      mediaConnectInputs.forEach((i) => { inputNames[i.id] = i.name; });
      commit('setInputNames', inputNames);

      const ids = mediaConnectInputs.map((input) => input.id);

      if (ids.length > 0) {
        dispatch('subscribeToAll', ids);
      } else {
        console.warn('[Notifications] No MEDIA_CONNECT inputs found to subscribe to');
      }
    } catch (err) {
      console.error('[Notifications] Failed to fetch inputs for subscription:', err);
    }
  },

  unsubscribe({ dispatch }) {
    dispatch('unsubscribeAll');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
